import 'src/styles/ui/resource-card.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * ResourceCard Component - Flexible card component for displaying resource information
 *
 * @param {Object} props
 * @param {string} props.title - Main title in the header
 * @param {string} props.subtitle - Subtitle in the header (optional)
 * @param {string} props.label - Optional badge label (e.g., "Group", "Active")
 * @param {string} props.customLabal - Additional optional badge label (e.g., "Base", "Support")
 * @param {Object} props.actionIcon - FontAwesome icon for action button (optional)
 * @param {Function} props.onAction - Callback when action button is clicked
 * @param {boolean} props.showActionButton - Whether to show action button
 * @param {string} props.actionButtonTitle - Tooltip text for action button
 * @param {React.ReactNode} props.children - Content body of the card
 * @param {string} props.className - Additional CSS classes
 */
const ResourceCard = ({
  title,
  subtitle,
  label,
  icon = null,
  actionIcon,
  onAction,
  showActionButton = false,
  actionButtonTitle,
  children,
  className,
  customLabel,
  ...props
}) => {
  const cardClasses = classNames('resource-card', className);

  return (
    <div className={cardClasses} {...props}>
      <div className='resource-card__header'>
        <div className='resource-card__info'>
          <div className='d-flex align-items-center'>
            {icon && <FontAwesomeIcon icon={icon} className='spacer-right' />}
            <div className='resource-card__title'>{title}</div>
          </div>
          {subtitle && <div className='resource-card__subtitle'>{subtitle}</div>}
        </div>
        <div className='resource-card__header-actions'>
          {label && <span className='resource-card__label'>{label}</span>}
          {customLabel && <span className='resource-card__label'>{customLabel}</span>}
          {showActionButton && actionIcon && (
            <button
              type='button'
              className='resource-card__action-btn'
              title={actionButtonTitle}
              onClick={onAction}
            >
              <FontAwesomeIcon icon={actionIcon} />
            </button>
          )}
        </div>
      </div>

      {children && <div className='resource-card__body'>{children}</div>}
    </div>
  );
};

ResourceCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  label: PropTypes.string,
  actionIcon: PropTypes.object,
  onAction: PropTypes.func,
  showActionButton: PropTypes.bool,
  actionButtonTitle: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.node,
  customLabel: PropTypes.string,
};

ResourceCard.defaultProps = {
  subtitle: null,
  label: null,
  actionIcon: null,
  onAction: null,
  showActionButton: false,
  actionButtonTitle: null,
  children: null,
  className: '',
  icon: null,
  customLabel: null,
};

export default ResourceCard;
